/**
 * @module nhcmap/MapInteractions
 */

class MapInteractions {
  /**
   * Creates an instance of MapInteractions.
   */
  constructor() {
    // Keep track of the interactions we've created
    this.interactions = [];
  }

  /**
   * Load map interactions from configuration objects.
   * This method is now called by MapManager when the map is initialized.
   * @param {object} configs 
   * @param {MapManager} mapManager
   */
  loadInteractionsFromConfigs(configs, mapManager) {
    // Merge interaction configurations from all products
    const allInteractionConfigs = configs.flatMap(config => config.interactions || []);
    const uniqueInteractionConfigs = this._getUniqueConfigs(allInteractionConfigs, 'type');

    // Create and add interactions to the map
    uniqueInteractionConfigs.forEach(async interactionConfig => {
      const interaction = await this.createInteraction(interactionConfig, mapManager);
      if (interaction) {
        this.interactions.push(interaction);
        mapManager.map.addInteraction(interaction);
      }
    });
  }

  /**
   * Creates a new interaction based on the provided configuration.
   * @param {object} config - The interaction configuration.
   * @param {MapManager} mapManager - The MapManager instance.
   * @returns {Promise<Interaction>} The created interaction instance.
   */
  async createInteraction(config, mapManager) {
    switch (config.type) {
      case 'autoZoom':
        const { default: AutoZoomInteraction } = await import("./interactions/AutoZoomInteraction.js");
        return new AutoZoomInteraction({
          skipLayers: ['nhcMapLayer', 'nhcBorderLayer', 'nhcLabelLayer', 'nhcGraticule', 'stateAbbrLayer'],
          latOffset: config.latOffset || 0.0,
          lonOffset: config.lonOffset || 0.0,
          mapManager: mapManager
        });
      // ... other cases
      default:
        return null;
    }
  }

  /**
   * Utility method to get unique configurations based on a key.
   * @param {Array} configs - Array of configuration objects.
   * @param {string} key - The key to determine uniqueness.
   * @returns {Array} Array of unique configuration objects.
   */
  _getUniqueConfigs(configs, key) {
    return [...new Map(configs.map(item => [item[key], item])).values()];
  }

}

export default MapInteractions;